import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Token storage (localStorage). The backend accepts BOTH cookies and
// `Authorization: Bearer <token>`. On iOS WKWebView, ITP unreliably drops
// cross-origin cookies, so the Bearer path is the source of truth for native.
// ─────────────────────────────────────────────────────────────────────────────
const TOKEN_KEY = 'cyb_at';
const REFRESH_KEY = 'cyb_rt';

const getAccessToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
const getRefreshToken = () => {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
};
const setTokens = (data) => {
  try {
    if (data?.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
    if (data?.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
  } catch { /* storage full / disabled — non-fatal */ }
};
const clearTokens = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch { /* ignore */ }
};

// ─────────────────────────────────────────────────────────────────────────────
// Native (Capacitor) detection — evaluated once at module load.
// ─────────────────────────────────────────────────────────────────────────────
const isNative = (() => {
  try {
    if (typeof window === 'undefined') return false;
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function') {
      return window.Capacitor.isNativePlatform();
    }
    if (window.location && window.location.origin && window.location.origin.startsWith('capacitor://')) {
      return true;
    }
  } catch { /* ignore */ }
  return false;
})();

// Diagnostic logger — visible in Safari Web Inspector when remote-debugging iOS.
const log = (...args) => { try { console.log('[CyAuth]', ...args); } catch { /* ignore */ } };
const warn = (...args) => { try { console.warn('[CyAuth]', ...args); } catch { /* ignore */ } };

// ─────────────────────────────────────────────────────────────────────────────
// One-time global axios bootstrap:
//   1. withCredentials: only on web (where we rely on cookies as a fallback).
//      Capacitor native uses Bearer tokens only — sending credentials triggers
//      the "wildcard CORS + credentials" rejection from WKWebView because the
//      preview ingress sets Access-Control-Allow-Origin: *.
//   2. Attach `Authorization: Bearer <token>` to every outgoing request.
//   3. Patch axios.create so the ~11 components that build their own axios
//      instances (axios.create({ baseURL: API })) also get the Bearer header.
// ─────────────────────────────────────────────────────────────────────────────
axios.defaults.withCredentials = !isNative;

function attachBearer(config) {
  try {
    // ── On native (Capacitor), FORCE withCredentials off regardless of what
    //    the caller passed. The preview ingress returns Access-Control-Allow-
    //    Origin: * which is rejected by WKWebView when credentials are sent.
    //    iOS auth is via Bearer token only, so credentials aren't needed.
    if (isNative) {
      config.withCredentials = false;
    }
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch { /* never block requests */ }
  return config;
}

// ─────────────────────────────────────────────────────────────────────────────
// JSON response normalization.
// iOS WKWebView occasionally delivers JSON bodies as raw strings to axios when
// the response is cross-origin from `capacitor://localhost`. Axios's built-in
// transformResponse only auto-parses when Content-Type matches its heuristic,
// which can fail through proxies. We forcibly parse any string body whose
// first non-whitespace char is `{` or `[` so downstream `data.findIndex` and
// `data.email` accesses don't silently break.
// ─────────────────────────────────────────────────────────────────────────────
function parseJsonResponse(response) {
  try {
    if (response && typeof response.data === 'string') {
      const trimmed = response.data.trim();
      if (trimmed && (trimmed[0] === '{' || trimmed[0] === '[')) {
        try {
          response.data = JSON.parse(trimmed);
          log('parsed string→JSON for', response.config?.url || '(unknown url)');
        } catch (e) {
          warn('JSON parse failed for', response.config?.url, e?.message);
        }
      }
    } else if (response && response.config?.url && response.config.url.includes('/api/auth/login')) {
      // One-shot diagnostic: confirms the interceptor is firing on the actual login call.
      log('login response interceptor fired. data type:', typeof response.data);
    }
  } catch { /* never block responses */ }
  return response;
}

if (typeof window !== 'undefined' && !window.__cyAuthBootstrapped) {
  window.__cyAuthBootstrapped = true;
  axios.interceptors.request.use(attachBearer);
  axios.interceptors.response.use(parseJsonResponse, (err) => {
    // Best-effort: ensure error responses are also JSON-parsed for `detail` extraction.
    try { if (err?.response) err.response = parseJsonResponse(err.response); } catch { /* ignore */ }
    return Promise.reject(err);
  });
  const _origCreate = axios.create.bind(axios);
  axios.create = function patchedAxiosCreate(config = {}) {
    const inst = _origCreate(config);
    inst.interceptors.request.use(attachBearer);
    inst.interceptors.response.use(parseJsonResponse, (err) => {
      try { if (err?.response) err.response = parseJsonResponse(err.response); } catch { /* ignore */ }
      return Promise.reject(err);
    });
    return inst;
  };
  // ── Startup diagnostic. If API_URL is undefined or empty, log a LOUD warning
  //    so we know immediately the build was missing REACT_APP_BACKEND_URL.
  if (!API_URL || typeof API_URL !== 'string' || !API_URL.startsWith('http')) {
    warn('!!! API_URL is missing or invalid !!! Value:', API_URL, '— all API calls will hit the local bundle and return HTML. Rebuild with REACT_APP_BACKEND_URL set.');
  } else {
    log('axios bootstrapped. isNative=', isNative, 'API_URL=', API_URL, 'origin=', typeof window !== 'undefined' ? window.location.origin : 'n/a');
  }
}

function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback || 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

function extractErrorMessage(error, action) {
  const detail = error?.response?.data?.detail;
  if (detail) return formatApiErrorDetail(detail);
  if (error?.response?.status >= 500) return `Our server hit a snag while ${action}. Please try again in a moment.`;
  if (error?.response?.status === 429) return 'Too many attempts. Please wait a few minutes and try again.';
  if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') return "Can't reach the server. Check your connection and try again.";
  if (error?.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (error?.message) return error.message;
  return `Something went wrong while ${action}. Please try again.`;
}

// Strip auth-only fields from the login/register response before holding it as user state.
function stripAuthFields(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const { access_token, refresh_token, ...rest } = payload; // eslint-disable-line no-unused-vars
  return rest;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const haveToken = !!getAccessToken();
    const haveRefresh = !!getRefreshToken();
    log('checkAuth start', { isNative, haveToken, haveRefresh });

    // ── Native fast-path: no cookies will ever be sent cross-origin from
    //    capacitor://localhost → preview URL. If we don't have a stored token,
    //    short-circuit straight to the login screen.
    if (isNative && !haveToken && !haveRefresh) {
      log('native + no stored tokens → /login');
      setUser(false);
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      // Defensive: if the response was hijacked by an SPA fallback (HTML), bail.
      if (typeof data !== 'object' || data === null || !data.id || !data.email) {
        warn('/auth/me returned unexpected payload, treating as logged out', data);
        clearTokens();
        setUser(false);
        return;
      }
      log('/auth/me ok', data.email);
      setUser(data);
    } catch (error) {
      log('/auth/me failed', error?.response?.status, error?.message);
      // Try refresh via cookie (web) OR stored refresh token (native).
      const rt = getRefreshToken();
      try {
        const { data: refData } = await axios.post(
          `${API_URL}/api/auth/refresh`,
          rt ? { refresh_token: rt } : {},
        );
        if (refData?.access_token) setTokens({ access_token: refData.access_token });
        const { data } = await axios.get(`${API_URL}/api/auth/me`);
        if (typeof data !== 'object' || data === null || !data.id || !data.email) {
          warn('/auth/me (post-refresh) returned unexpected payload', data);
          clearTokens();
          setUser(false);
          return;
        }
        log('/auth/me ok after refresh', data.email);
        setUser(data);
      } catch (err2) {
        log('refresh failed', err2?.response?.status, err2?.message, '→ logged out');
        clearTokens();
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const fullUrl = `${API_URL}/api/auth/login`;
    log('login → POST', fullUrl, '(API_URL =', API_URL || '<<UNDEFINED!>>', ')');
    try {
      const resp = await axios.post(fullUrl, { email, password });
      let data = resp.data;
      log('login resp. status:', resp.status, 'finalUrl:', resp.request?.responseURL || resp.config?.url, 'content-type:', resp.headers?.['content-type'], 'typeof:', typeof data);
      // ── Defensive: if axios didn't auto-parse JSON (some WKWebView edge cases),
      //    parse the string body ourselves so downstream state isn't garbage.
      if (typeof data === 'string') {
        const preview = data.slice(0, 80);
        warn('login: response body is a string (not parsed JSON). Preview:', preview);
        try { data = JSON.parse(data); log('login: parsed string body, keys:', Object.keys(data || {})); }
        catch (e) {
          warn('login: could not parse string body. The server returned non-JSON (likely HTML). Aborting login.', e?.message);
          return { success: false, error: 'Server returned an unexpected response. Please check your network or try again.' };
        }
      }
      // ── Sanity: a successful login MUST contain an access_token and an id/email.
      //    Without these we won't be able to authenticate any future request.
      if (!data || typeof data !== 'object' || !data.access_token || !data.id) {
        warn('login: response missing access_token or id. Got keys:', data && typeof data === 'object' ? Object.keys(data) : '<<not object>>');
        return { success: false, error: 'Login response was incomplete. Please try again.' };
      }
      setTokens(data);
      const userObj = stripAuthFields(data);
      log('login ok. email=', userObj?.email, 'role=', userObj?.role, 'id=', userObj?.id);
      setUser(userObj);
      return { success: true, user: userObj };
    } catch (error) {
      warn('login failed', error?.response?.status, error?.message);
      return { success: false, error: extractErrorMessage(error, 'signing you in') };
    }
  };

  const register = async (email, password, name, username) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/register`, { email, password, name, username });
      setTokens(data);
      const userObj = stripAuthFields(data);
      setUser(userObj);
      return { success: true, user: userObj };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error, 'creating your account') };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (error) {
      warn('logout request errored (continuing local cleanup)', error?.message);
    } finally {
      clearTokens();
      // Apple Guideline 5.1.1(i) — AI consent is a per-user, per-device choice.
      // Clearing on logout means the next user signing in on this device gets
      // re-prompted instead of inheriting the prior account's consent.
      try {
        localStorage.removeItem('cyb_ai_consent');
        localStorage.removeItem('cyb_ai_consent_in_app');
        localStorage.removeItem('cyb_eula_version');
        localStorage.removeItem('cyb_eula_seen_in_app');
      } catch { /* ignore */ }
      setUser(false);
    }
  };

  const updateUser = (updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
